import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /**
   * Bumped every time a cart mutation settles. An in-flight `getCart()` read is
   * discarded when this moves, so a slow read can never overwrite a newer count.
   * @type {number}
   */
  #cartUpdateGeneration = 0;

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
    }
  };

  /**
   * Handles the cart update event.
   * @param {CartLinesUpdateEvent} event - The cart update event.
   */
  onCartUpdate = (event) => {
    event.promise
      ?.then(({ cart, detail }) => {
        const itemCount = cart?.totalQuantity ?? detail?.itemCount ?? 0;

        this.#cartUpdateGeneration += 1;
        this.renderCartBubble(itemCount);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-icon] Event promise rejected:', error);
      });
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The absolute number of items in the cart.
   * @param {boolean} [animate=true] - Whether to animate the bubble.
   * @param {boolean} [stamp=true] - Re-date the sessionStorage snapshot. Pass `false`
   *   when the value came OUT of the snapshot, or a stale count renews its own
   *   freshness on every navigation and the window below never bites.
   */
  renderCartBubble = async (itemCount, animate = true, stamp = true) => {
    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);

    this.currentCartCount = itemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    if (stamp) {
      sessionStorage.setItem(
        'cart-count',
        JSON.stringify({
          value: String(this.currentCartCount),
          timestamp: Date.now(),
        })
      );
    }

    if (!animate || itemCount === 0) return;

    // Ensure element is visible before starting animation
    // Use requestAnimationFrame to ensure the browser sees the state change
    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Reconciles the rendered count against the real cart.
   *
   * Spec 061 §6.3, handed to U5. Horizon reconciled against a sessionStorage snapshot
   * and nothing else, inside a 10-second window. The snapshot was the bug: since U10b
   * the theme prefetches documents with speculation rules, and Chromium holds a
   * prefetched document in its cache for MINUTES. Hover a card (prefetch fires) → add
   * to the cart → wait → click the card, and the restored document renders the count
   * from before the add, on a page that loaded instantly. No window fixes that — a
   * stale snapshot is still a snapshot, and any window is a guess about how long the
   * browser will keep the document.
   *
   * So the snapshot is demoted to a first paint and the truth comes from
   * `Shopify.actions.getCart()` — the Standard Action, which is what AGENTS.md means
   * by "Standard Actions own cart state; no duplicated cart state in JS". 061 §6.3 is
   * explicit that this must NOT be solved with a cart-count partial: ADR-004 records
   * `{% partial %}` as unavailable on this store, and an unguarded one raises
   * `Unknown tag`. Demoting the snapshot is the fix. DELETING its freshness window was
   * NOT part of the fix and was its own regression — see `#paintFromSessionSnapshot`.
   *
   * Two properties worth stating, because each is a way this could have gone wrong:
   *   - It cannot fight a live mutation. `onCartUpdate` writes the authoritative
   *     count from the mutation's own promise; a `getCart()` reply that lands after
   *     one would overwrite it with an older read, so the response is dropped when a
   *     mutation settled while it was in flight. The guard counts mutations rather
   *     than comparing counts, because `currentCartCount` is deliberately unreadable
   *     above 99 — the setter blanks the text there — and comparing it would make
   *     the guard misfire on exactly the carts it matters least to get wrong.
   *   - It runs on connect and on `pageshow` (bfcache and prefetch restore), which
   *     is exactly the pair 061 §6.3 asks for. It is not polled.
   */
  ensureCartBubbleIsCorrect = () => {
    // Ensure refs are available
    if (!this.refs.cartBubbleCount) return;

    this.#paintFromSessionSnapshot();
    this.#hydrateFromCart();
  };

  /** Horizon's window, kept: long enough to cover "mutate, then navigate". */
  static SNAPSHOT_FRESH_MS = 10_000;

  /**
   * True when this document body did not come fresh off the network (prefetched or
   * HTTP-cached), so its count may be arbitrarily old and the snapshot wins at any age.
   * MEASURED Chrome 148: `deliveryType` exists and is `''` on a network navigation; the
   * non-empty values are unverified here, hence "not `''`" rather than a string match.
   * Absent or unset ⇒ false ⇒ the window decides alone, which is Horizon.
   */
  #documentBodyIsFromCache() {
    const nav = performance.getEntriesByType('navigation')[0];
    return typeof nav?.deliveryType === 'string' && nav.deliveryType !== '';
  }

  /**
   * Paints the last known count when the snapshot is plausibly NEWER than the document
   * — written seconds ago, or the body came out of a cache. Otherwise the server render
   * stands.
   *
   * ⚠️ That condition is load-bearing and was once deleted, which turned this from
   * "paint a value that may be newer than the document" into "paint one that may be
   * OLDER" — and the document is usually right. Snapshot "3", shopper checks out in the
   * same tab and returns: the cart is empty, the server renders 0 correctly, and an
   * unconditional snapshot repaints "3" over it; with `Shopify.actions` missing (the
   * case the deleted comment called harmless) the wrong number then stays.
   *
   * 061 §6.3 stays fixed twice over — cache detection paints a restored document at
   * once, `#hydrateFromCart` corrects it regardless. Only with both unavailable does a
   * stale page keep its count, which is where Horizon left it.
   */
  #paintFromSessionSnapshot() {
    const sessionStorageCount = sessionStorage.getItem('cart-count');
    if (sessionStorageCount === null) return;

    const visibleCount = this.refs.cartBubbleCount.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);
      if (value === visibleCount) return;

      const isFresh = Date.now() - timestamp < CartIcon.SNAPSHOT_FRESH_MS;
      if (!isFresh && !this.#documentBodyIsFromCache()) return;

      const count = parseInt(value, 10);
      // `stamp: false` — this value came out of the snapshot, so re-dating it would
      // let a stale count renew its own freshness on every navigation.
      if (count >= 0) this.renderCartBubble(count, false, false);
    } catch (_) {
      // Malformed snapshot: ignore it and let the hydrate below settle the count.
    }
  }

  /**
   * Asks the platform for the real cart and corrects the bubble if it differs.
   *
   * `Shopify.actions` is attached by a module script, so on a cold document this
   * element can upgrade before it exists. Same shape as
   * `assets/standard-actions-override.js`: run now if it is there, otherwise once on
   * DOMContentLoaded, which fires after every module script has executed.
   */
  #hydrateFromCart() {
    if (window.Shopify?.actions?.getCart) {
      this.#requestCart();
      return;
    }

    document.addEventListener('DOMContentLoaded', () => this.#requestCart(), { once: true });
  }

  #requestCart() {
    const actions = window.Shopify?.actions;
    if (typeof actions?.getCart !== 'function') return;

    const generationBefore = this.#cartUpdateGeneration;

    actions
      .getCart()
      .then(({ cart }) => {
        // A null cart is not a failure — it is a shopper with no cart cookie, i.e. zero.
        const itemCount = cart == null ? 0 : cart.totalQuantity;
        if (typeof itemCount !== 'number') return;

        // A mutation settled while this read was in flight; its count is newer.
        if (this.#cartUpdateGeneration !== generationBefore) return;
        if (!this.isConnected) return;

        this.renderCartBubble(itemCount, false);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-icon] getCart failed:', error);
      });
  }
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
